/**
 * 项目元素详情 API Hooks
 */
import { useQuery, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { projectElementsApi } from '@/services/api/project-elements';
import type {
  CharacterDetailResponse,
  LocationDetailResponse,
  PropDetailResponse,
} from '@/services/api/project-elements';

export type ElementType = 'character' | 'location' | 'prop';

const ELEMENT_DETAIL_QUERY_KEY_VERSION = 'v1' as const;

// Query Keys
export const elementDetailKeys = {
  all: ['element-details', ELEMENT_DETAIL_QUERY_KEY_VERSION] as const,
  characters: () => [...elementDetailKeys.all, 'character'] as const,
  character: (parentProjectId: string, characterId: string) =>
    [...elementDetailKeys.characters(), parentProjectId, characterId] as const,
  locations: () => [...elementDetailKeys.all, 'location'] as const,
  location: (parentProjectId: string, locationId: string) =>
    [...elementDetailKeys.locations(), parentProjectId, locationId] as const,
  props: () => [...elementDetailKeys.all, 'prop'] as const,
  prop: (parentProjectId: string, propId: string) =>
    [...elementDetailKeys.props(), parentProjectId, propId] as const,
};

/**
 * 获取角色详情（包含标签和资源）
 */
export function useCharacterDetail(parentProjectId: string | null, characterId: string | null) {
  return useQuery<CharacterDetailResponse>({
    queryKey: elementDetailKeys.character(parentProjectId!, characterId!),
    queryFn: () => projectElementsApi.getCharacterDetail(parentProjectId!, characterId!),
    enabled: !!parentProjectId && !!characterId,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 获取场景详情（包含标签和资源）
 */
export function useLocationDetail(parentProjectId: string | null, locationId: string | null) {
  return useQuery<LocationDetailResponse>({
    queryKey: elementDetailKeys.location(parentProjectId!, locationId!),
    queryFn: () => projectElementsApi.getLocationDetail(parentProjectId!, locationId!),
    enabled: !!parentProjectId && !!locationId,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 获取道具详情（包含标签和资源）
 */
export function usePropDetail(parentProjectId: string | null, propId: string | null) {
  return useQuery<PropDetailResponse>({
    queryKey: elementDetailKeys.prop(parentProjectId!, propId!),
    queryFn: () => projectElementsApi.getPropDetail(parentProjectId!, propId!),
    enabled: !!parentProjectId && !!propId,
    staleTime: 1000 * 60 * 5, // 5分钟
    gcTime: 1000 * 60 * 30, // 30分钟
  });
}

/**
 * 失活指定元素的详情缓存。
 * 在更新角色/场景/道具的任何属性（名称、封面、标签）后调用。
 */
export function invalidateElementDetail(
  queryClient: QueryClient,
  projectId: string,
  type: ElementType,
  elementId: string
): void {
  const queryKey =
    type === 'character'
      ? elementDetailKeys.character(projectId, elementId)
      : type === 'location'
        ? elementDetailKeys.location(projectId, elementId)
        : elementDetailKeys.prop(projectId, elementId);
  void queryClient.invalidateQueries({ queryKey });
}

/**
 * 读取指定类型下所有元素详情缓存（用于乐观更新前备份）。
 * 返回 [queryKey, data][] 元组数组。
 */
export function getElementDetailQueriesData(
  queryClient: QueryClient,
  projectId: string,
  type: ElementType
): [QueryKey, unknown][] {
  const prefix =
    type === 'character'
      ? elementDetailKeys.characters()
      : type === 'location'
        ? elementDetailKeys.locations()
        : elementDetailKeys.props();
  return queryClient
    .getQueriesData<unknown>({ queryKey: prefix })
    .filter(([key]) => Array.isArray(key) && key.includes(projectId));
}

/**
 * 更新指定 queryKey 对应的元素详情缓存数据（用于乐观更新）。
 */
export function setElementDetailData(
  queryClient: QueryClient,
  queryKey: QueryKey,
  updater: (old: unknown) => unknown
): void {
  queryClient.setQueryData(queryKey, updater);
}

/**
 * 失活所有元素详情缓存，并强制刷新当前活跃查询。
 * 在无法确定具体元素类型/ID 的场景（如删除素材、批量操作）后调用。
 */
export function invalidateAllElementDetails(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: elementDetailKeys.all });
  void queryClient.refetchQueries({ queryKey: elementDetailKeys.all, type: 'active' });
}

/**
 * 取消指定类型下所有元素详情的进行中请求（乐观更新前调用）。
 */
export function cancelElementDetailQueriesByType(
  queryClient: QueryClient,
  type: ElementType
): Promise<void> {
  const prefix =
    type === 'character'
      ? elementDetailKeys.characters()
      : type === 'location'
        ? elementDetailKeys.locations()
        : elementDetailKeys.props();
  return queryClient.cancelQueries({ queryKey: prefix });
}

/**
 * 读取指定类型下所有元素详情缓存（跨 projectId），用于乐观更新前数据备份。
 */
export function getElementDetailQueriesDataByType<T>(
  queryClient: QueryClient,
  type: ElementType
): [QueryKey, T | undefined][] {
  const prefix =
    type === 'character'
      ? elementDetailKeys.characters()
      : type === 'location'
        ? elementDetailKeys.locations()
        : elementDetailKeys.props();
  return queryClient.getQueriesData<T>({ queryKey: prefix });
}

/**
 * 失活指定类型下所有元素详情缓存（跨 projectId）。
 */
export function invalidateElementDetailsByType(queryClient: QueryClient, type: ElementType): void {
  const prefix =
    type === 'character'
      ? elementDetailKeys.characters()
      : type === 'location'
        ? elementDetailKeys.locations()
        : elementDetailKeys.props();
  void queryClient.invalidateQueries({ queryKey: prefix });
}

export function refetchActiveElementDetails(queryClient: QueryClient): Promise<void> {
  return queryClient.refetchQueries({ queryKey: elementDetailKeys.all, type: 'active' });
}

export function invalidateElementDetailCharacter(
  queryClient: QueryClient,
  projectId: string,
  characterId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: elementDetailKeys.character(projectId, characterId),
  });
}

export function invalidateElementDetailLocation(
  queryClient: QueryClient,
  projectId: string,
  locationId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: elementDetailKeys.location(projectId, locationId),
  });
}

export function invalidateElementDetailProp(
  queryClient: QueryClient,
  projectId: string,
  propId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: elementDetailKeys.prop(projectId, propId),
  });
}
